import { get, isUndefined } from 'lodash-es';

import { castDataType } from './cast-data-types.js';
import { XYAxisTypeTime } from '../types/axis.js';
import { isCategorical, isTime } from '../types/guards.js';
import { ScaleTypes, ScaleTypesMap } from '../types/scale-types-map.js';
import {
  type RectDatapointInternal,
  type SeriesPaths,
  XYChartAxisInternal,
  type XYValueAccessorDataTypes,
} from '../types/xy-chart-internals.js';
import {
  DotDatapoint,
  type XYAccessorDataTypes,
  type XYChartData,
} from '../types/xy-chart.js';

export function getDatumValue(
  datum: XYChartData,
  path: string[] | undefined,
): XYAccessorDataTypes | undefined {
  if (!path) {
    return undefined;
  }

  return get(datum, path);
}

export function getPoint<T extends ScaleTypes>(
  datum: XYChartData,
  path: string[] | undefined,
  axisType: T,
): ScaleTypesMap[T]['castDataType'] {
  const value = getDatumValue(datum, path);

  return castDataType(value, axisType);
}

/**
 * Generates a dot data point from the provided datum and series properties.
 *
 * @param datum - The individual data point.
 * @param path - The series path properties for the dot.
 * @param xAxis - The x-axis internal configuration.
 * @param yAxis - The y-axis internal configuration.
 * @returns - The dot series data point, or undefined if any required value is missing or invalid.
 */
export function getDotDatapoint(
  datum: XYChartData,
  paths: SeriesPaths,
  xAxis: XYChartAxisInternal,
  yAxis: XYChartAxisInternal,
): DotDatapoint | undefined {
  const x0 = getPoint(datum, paths.x0, xAxis.type);
  const y0 = getPoint(datum, paths.y0, yAxis.type);
  let x1;
  let y1;

  if (isUndefined(x0) || isUndefined(y0)) {
    return;
  }

  if (isTime(xAxis.type) && paths.x1) {
    x1 = getPoint(datum, paths.x1, XYAxisTypeTime);
  }

  if (isTime(yAxis.type) && paths.y1) {
    y1 = getPoint(datum, paths.y1, XYAxisTypeTime);
  }

  return { ...datum, x0, x1, y0, y1 };
}

/**
 * Generates a rect data point from the provided datum and series properties.
 *
 * @param datum - The individual data point.
 * @param paths - The series paths properties for the rect.
 * @param xAxis - The x-axis internal configuration.
 * @param yAxis - The y-axis internal configuration.
 * @returns - The rect data point, or undefined if any required value is missing or invalid.
 */
export function getRectDatapoint(
  datum: XYChartData,
  paths: SeriesPaths,
  xAxis: XYChartAxisInternal,
  yAxis: XYChartAxisInternal,
): RectDatapointInternal | undefined {
  const x0 = getPoint(datum, paths.x0, xAxis.type);

  if (x0 === undefined) {
    return undefined;
  }

  const x1 = paths.x1 && getPoint(datum, paths.x1, xAxis.type);

  if (x1 === undefined && !isCategorical(xAxis.type)) {
    return undefined;
  }

  const y0 = getPoint(datum, paths.y0, yAxis.type);

  if (y0 === undefined) {
    return undefined;
  }

  const y1 = paths.y1 && getPoint(datum, paths.y1, yAxis.type);

  if (y1 === undefined && !isCategorical(yAxis.type)) {
    return undefined;
  }

  const value = getDatumValue(datum, paths.value) as XYValueAccessorDataTypes;

  return {
    ...datum,
    value,
    x0,
    x1,
    y0,
    y1,
  };
}
